import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useTenantContext } from 'src/context/useTenantContext';
import { tenantEmployeeDayOff } from 'src/services/apis/tenantRequests';
import { QUERIES } from 'src/utils/react-query';

export const useEmployeeDayOff = () => {
  const { orgSlug, restaurantId } = useTenantContext();
  return useQuery([QUERIES.EMPLOYEE_DAY_OFF, orgSlug, restaurantId], () =>
    tenantEmployeeDayOff({ orgSlug, restaurantId })
  );
};

export const useEmployeeMonthlyDayOff = (employeeId: string | null) => {
  const { orgSlug, restaurantId } = useTenantContext();
  const [page] = useState(1);
  const [filters, setFilters] = useState<{
    limit: number;
    startDate: Date | null | string;
    endDate: Date | null | string;
    employeeId: string | null;
  }>({
    limit: 100,
    startDate: dayjs().startOf('month').toDate().toISOString(),
    endDate: dayjs().endOf('month').endOf('day').toDate().toISOString(),
    employeeId: null,
  });
  const getDefaultFilters = useCallback(() => {
    return {
      limit: 100,
      startDate: dayjs().startOf('month').toDate().toISOString(),
      endDate: dayjs().endOf('month').endOf('day').toDate().toISOString(),
      employeeId: employeeId,
    };
  }, [employeeId]);
  // Reset the filters when the scope changes, derived during render rather than in an
  // effect: React re-renders with the new value before committing, where the effect
  // committed the stale filters first and then immediately replaced them.
  const filterScope = String(employeeId);
  const [previousFilterScope, setPreviousFilterScope] = useState(filterScope);
  if (previousFilterScope !== filterScope) {
    setPreviousFilterScope(filterScope);
    setFilters(getDefaultFilters());
  }
  const query = useQuery(
    [
      QUERIES.EMPLOYEE_MONTHLY_DAY_OFF,
      orgSlug,
      restaurantId,
      page,
      {
        employeeId: employeeId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    ],
    ({ queryKey: [_key, _orgSlug, _restaurantId, _page, _filters] }: { queryKey: any }) => {
      const convertedFilters: string[] = [];
      convertedFilters.push(`employeeId||$eq||${_filters.employeeId}`);
      convertedFilters.push(`date||$gte||${_filters.startDate}`);
      convertedFilters.push(`date||$lte||${_filters.endDate}`);
      return tenantEmployeeDayOff(
        { orgSlug: _orgSlug as string, restaurantId: _restaurantId as string },
        {
          page: _page as number,
          limit: _filters.limit,
          filter: convertedFilters,
        }
      );
    },
    {
      cacheTime: 0,
      refetchOnMount: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!employeeId,
    }
  );
  return {
    query,
  };
};
